import React from 'react';
import {i18n} from '@netx/core/i18n';
import {Button, Typography} from '@mui/material';
import {Dialog, type DialogProps} from '@netx/core-components/Dialog';

export interface ConfirmationOptions
	extends Pick<DialogProps, 'open' | 'title'> {
	cancelLabel?: string;
	confirmLabel?: string;
	disableCancel?: boolean;
	message: React.ReactNode;
	DialogActions?: React.ComponentType<{
		onSubmit(): void;
		onClose?: () => void;
	}>;
	DialogProps?: Partial<Omit<DialogProps, 'onClose'>>;
}

export interface ConfirmationDialogProps extends ConfirmationOptions {
	onSubmit(...args: any[]): void;
	onClose(): void;
}

export const ConfirmationDialog: React.FunctionComponent<
	ConfirmationDialogProps
> = ({
	cancelLabel,
	confirmLabel,
	message,
	disableCancel,
	open,
	title,
	onSubmit,
	onClose,
	DialogActions,
	DialogProps,
	...props
}) => {
	cancelLabel = cancelLabel ?? i18n.t('i18n.cancel');
	confirmLabel =
		confirmLabel ??
		(disableCancel ? i18n.t('i18n.ok') : i18n.t('i18n.confirm'));

	return (
		<Dialog
			aria-describedby="dialog-message"
			title={title}
			{...DialogProps}
			dialogActions={
				DialogActions ? (
					<DialogActions onSubmit={onSubmit} onClose={onClose} />
				) : (
					<>
						{!disableCancel ? (
							<Button
								onClick={onClose}
								color="primary"
								data-testid="confirmation-cancel"
							>
								{cancelLabel}
							</Button>
						) : null}
						<Button
							onClick={onSubmit}
							color="primary"
							data-testid="confirmation-confirm"
						>
							{confirmLabel}
						</Button>
					</>
				)
			}
			open={open}
			onClose={onClose}
		>
			<Typography
				color="textSecondary"
				component="div"
				id="dialog-message"
				paragraph
				variant="body1"
			>
				{message}
			</Typography>
		</Dialog>
	);
};
